import api from "./axios";

/* ──────────────────────────────────────────────────────────────
 * OWNER
 *  - GET /owners/profile
 * ────────────────────────────────────────────────────────────── */

export async function fetchOwnerProfile() {
  const { data } = await api.get("/owners/profile");
  return data;
}

/* ──────────────────────────────────────────────────────────────
 * VEHICLES (Owner)
 * ────────────────────────────────────────────────────────────── */

async function resolveOwnerId(ownerId) {
  if (ownerId && ownerId !== "current-owner") return ownerId;
  const profile = await fetchOwnerProfile();
  const id = profile?.id;
  if (!id) throw new Error("Unable to resolve current owner id from profile");
  return id;
}

function normalizeOwnerVehicle(raw) {
  if (!raw || typeof raw !== "object") return raw;

  const seatsRaw =
    raw.seatingCapacity ?? raw.seats ?? raw.capacity ?? raw.seating_capacity;
  const seatsNum = Number(seatsRaw);

  const photos = Array.isArray(raw.photos)
    ? raw.photos.filter(Boolean)
    : Array.isArray(raw.photoUrls)
    ? raw.photoUrls.filter(Boolean)
    : [];

  return {
    ...raw,
    id: raw.id ?? raw.registrationNumber ?? raw.vehicleId ?? null,
    registrationNumber:
      raw.registrationNumber ?? raw.regNo ?? raw.vehicleRegNo ?? raw.registration_no ?? "",
    brand: raw.brand ?? raw.make ?? "",
    model: raw.model ?? raw.vehicleModel ?? raw.name ?? "",
    type: raw.type ?? raw.vehicleType ?? raw.category ?? "",
    fuelType: raw.fuelType ?? raw.fuel ?? raw.fuel_type ?? "",
    color: raw.color ?? raw.colour ?? "",
    averagePrice: raw.averagePrice ?? raw.avgPrice ?? raw.average_price ?? "",
    mileage: raw.mileage ?? raw.mileageKm ?? raw.mileage_km ?? "",
    yearOfPurchase:
      raw.yearOfPurchase ?? raw.purchaseYear ?? raw.year ?? raw.manufactureYear ?? "",
    vehicleLocation: raw.vehicleLocation ?? raw.location ?? raw.city ?? "",
    openForBookingStates:
      raw.openForBookingStates ?? raw.bookingStates ?? raw.openStates ?? "",
    seatingCapacity: Number.isFinite(seatsNum) ? seatsNum : seatsRaw ?? null,
    latitude: raw.latitude ?? "",
    longitude: raw.longitude ?? "",
    ratingAverage: raw.ratingAverage ?? raw.rating ?? null,
    ratingCount: raw.ratingCount ?? raw.reviewCount ?? 0,
    photos,
    complianceVerified:
      raw.complianceVerified === undefined ? null : raw.complianceVerified,
    complianceComplete:
      raw.complianceComplete === undefined ? null : raw.complianceComplete,
  };
}

export async function fetchOwnerVehiclesWithBookings(ownerId) {
  const effectiveOwnerId = await resolveOwnerId(ownerId);
  const { data } = await api.get(
    `/api/vehicles/by-owner-with-bookings/${effectiveOwnerId}`
  );
  return Array.isArray(data) ? data.map(normalizeOwnerVehicle) : [];
}

export async function fetchOwnerVehicles(ownerId) {
  const effectiveOwnerId = await resolveOwnerId(ownerId);
  const { data } = await api.get(
    `/api/vehicles/by-owner-with-bookings/${effectiveOwnerId}`
  );
  return Array.isArray(data) ? data.map(normalizeOwnerVehicle) : [];
}

export async function saveOwnerVehicle(payload) {
  const { data } = await api.post("/api/vehicles/save", payload);
  return data;
}

export async function updateOwnerVehicle(registrationNumber, payload) {
  if (!registrationNumber) {
    throw new Error("registrationNumber is required to update a vehicle");
  }
  const safeReg = encodeURIComponent(registrationNumber);
  const { data } = await api.put(`/api/vehicles/${safeReg}`, payload);
  return data;
}

export async function deleteOwnerVehicle(registrationNumber) {
  if (!registrationNumber) {
    throw new Error("registrationNumber is required to delete a vehicle");
  }
  const safeReg = encodeURIComponent(registrationNumber);
  const { data } = await api.delete(`/api/vehicles/${safeReg}`);
  return data;
}

/* ──────────────────────────────────────────────────────────────
 * DRIVERS (Owner)
 * ────────────────────────────────────────────────────────────── */

export async function fetchOwnerDrivers(ownerId, { includeSelf = false } = {}) {
  const effectiveOwnerId = await resolveOwnerId(ownerId);
  const { data } = await api.get(`/drivers/by-owner/${effectiveOwnerId}`, {
    params: includeSelf ? { includeSelf: true } : undefined,
  });
  return Array.isArray(data) ? data : [];
}

/* ──────────────────────────────────────────────────────────────
 * UPLOADS
 * ────────────────────────────────────────────────────────────── */

const multipartParamModeCache = {
  documents: "primary",
  photos: "primary",
};

function getUploadBucket(url) {
  if (url.includes("/documents")) return "documents";
  if (url.includes("/photos")) return "photos";
  return "default";
}

function buildMultipartForm(file) {
  const form = new FormData();
  form.append("file", file);
  return form;
}

async function sendMultipart(url, file, paramKey, paramVal, timeout = 120000) {
  const form = buildMultipartForm(file);

  const { data } = await api.post(url, form, {
    params: { [paramKey]: paramVal },
    timeout,
  });

  return data;
}

function shouldFallbackUpload(err) {
  const status = err?.response?.status;
  const body = err?.response?.data;

  if (![400, 404, 405].includes(status)) return false;

  const rawText =
    typeof body === "string"
      ? body
      : body?.message || body?.error || body?.detail || "";

  const msg = String(rawText || "").toLowerCase();

  return (
    msg.includes("doctype") ||
    msg.includes("phototype") ||
    msg.includes("type") ||
    msg.includes("required request parameter") ||
    msg.includes("required parameter") ||
    msg.includes("missing") ||
    status === 404 ||
    status === 405
  );
}

async function postMultipartWithParam(url, file, primaryParamKey, paramVal) {
  const bucket = getUploadBucket(url);
  const cachedMode = multipartParamModeCache[bucket] || "primary";

  const tryPrimary = () => sendMultipart(url, file, primaryParamKey, paramVal);
  const tryFallback = () => sendMultipart(url, file, "type", paramVal);

  if (cachedMode === "fallback") {
    try {
      return await tryFallback();
    } catch (err) {
      if (shouldFallbackUpload(err)) {
        const data = await tryPrimary();
        multipartParamModeCache[bucket] = "primary";
        return data;
      }
      throw err;
    }
  }

  try {
    const data = await tryPrimary();
    multipartParamModeCache[bucket] = "primary";
    return data;
  } catch (err) {
    if (!shouldFallbackUpload(err)) throw err;

    const data = await tryFallback();
    multipartParamModeCache[bucket] = "fallback";
    return data;
  }
}

export async function uploadVehicleDocument(registrationNumber, file, docType) {
  if (!registrationNumber || !file) return null;
  const safeReg = encodeURIComponent(registrationNumber);
  const type = docType || "OTHER";

  return postMultipartWithParam(
    `/api/vehicles/${safeReg}/documents`,
    file,
    "docType",
    type
  );
}

export async function uploadVehiclePhoto(registrationNumber, file, slot) {
  if (!registrationNumber || !file) return null;
  const safeReg = encodeURIComponent(registrationNumber);
  const type = slot || "OTHER";

  return postMultipartWithParam(
    `/api/vehicles/${safeReg}/photos`,
    file,
    "photoType",
    type
  );
}

/* ──────────────────────────────────────────────────────────────
 * LOCATION HELPERS
 * ────────────────────────────────────────────────────────────── */

function normalizeLocationSuggestion(item) {
  if (!item) return null;
  if (typeof item === "string") {
    const text = item.trim();
    return text ? { placeId: "", primaryText: text, secondaryText: "", fullText: text, latitude: null, longitude: null, source: "" } : null;
  }
  const placeId = item.placeId ?? item.place_id ?? "";
  const primaryText = item.primaryText ?? item.mainText ?? item.structured_formatting?.main_text ?? item.displayName ?? item.name ?? "";
  const secondaryText = item.secondaryText ?? item.secondary_text ?? item.structured_formatting?.secondary_text ?? item.formattedAddress ?? "";
  const fullText = item.fullText ?? item.description ?? [primaryText, secondaryText].filter(Boolean).join(", ");
  const latitude = item.latitude == null ? null : Number(item.latitude);
  const longitude = item.longitude == null ? null : Number(item.longitude);
  const source = item.source ?? "";
  const text = String(fullText || "").trim();
  if (!text) return null;
  return {
    placeId: String(placeId || "").trim(),
    primaryText: String(primaryText || text).trim(),
    secondaryText: String(secondaryText || "").trim(),
    fullText: text,
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null,
    source: String(source || "").trim(),
  };
}

export async function autocompleteVehicleLocation(input, opts = {}) {
  const query = String(input || "").trim();
  if (!query) return [];

  const config = { params: {} };
  if (opts.lat != null && Number.isFinite(Number(opts.lat))) config.params.lat = Number(opts.lat);
  if (opts.lng != null && Number.isFinite(Number(opts.lng))) config.params.lng = Number(opts.lng);
  if (opts.sessionToken) config.params.sessionToken = String(opts.sessionToken).trim();
  if (opts.signal) config.signal = opts.signal;

  const { data } = await api.get(
    `/api/vehicles/autocomplete/location-predictions/${encodeURIComponent(query)}`,
    config
  );

  const raw = Array.isArray(data) ? data : Array.isArray(data?.predictions) ? data.predictions : [];
  return raw.map(normalizeLocationSuggestion).filter(Boolean);
}

export async function resolveVehicleLatLng(location) {
  if (!location || !location.trim()) return null;
  const { data } = await api.get("/api/vehicles/resolve-latlng", {
    params: { location },
    timeout: 30000,
  });
  return data;
}

export async function reverseGeocodeVehicleLocation(lat, lng) {
  const safeLat = encodeURIComponent(String(lat));
  const safeLng = encodeURIComponent(String(lng));
  const { data } = await api.get(
    `/api/vehicles/reverse-geocode?lat=${safeLat}&lng=${safeLng}`,
    { timeout: 30000 }
  );
  return data;
}

/* ──────────────────────────────────────────────────────────────
 * COMPLIANCE
 * ────────────────────────────────────────────────────────────── */

export async function fetchVehicleCompliance(registrationNumber) {
  if (!registrationNumber) {
    throw new Error("registrationNumber is required");
  }

  const safeReg = encodeURIComponent(registrationNumber);

  try {
    const { data } = await api.get(`/api/vehicles/${safeReg}/compliance`, {
      timeout: 30000,
    });
    return data;
  } catch (err) {
    const code = err?.response?.status;
    if (code && [404, 405].includes(code)) return null;
    throw err;
  }
}

export async function fetchVehicleComplianceSafe(registrationNumber) {
  if (!registrationNumber) return null;

  try {
    return await fetchVehicleCompliance(registrationNumber);
  } catch (err) {
    const code = err?.response?.status;
    if (code && [404, 405].includes(code)) return null;
    throw err;
  }
}

export async function verifyVehicleFromRegistry(registrationNumber) {
  if (!registrationNumber) {
    throw new Error("registrationNumber is required");
  }

  const safeReg = encodeURIComponent(registrationNumber);
  const { data } = await api.post(
    `/api/vehicles/${safeReg}/verify-from-registry`,
    null,
    { timeout: 45000 }
  );
  return data;
}

/* ──────────────────────────────────────────────────────────────
 * FILE VIEW / DOWNLOAD
 * ────────────────────────────────────────────────────────────── */

function parseFilenameFromContentDisposition(cd) {
  if (!cd || typeof cd !== "string") return null;

  const star = cd.match(/filename\*\s*=\s*([^;]+)/i);
  if (star && star[1]) {
    const v = star[1]
      .trim()
      .replace(/^UTF-8''/i, "")
      .replace(/^"(.*)"$/, "$1");
    try {
      return decodeURIComponent(v);
    } catch {
      return v;
    }
  }

  const normal = cd.match(/filename\s*=\s*([^;]+)/i);
  if (normal && normal[1]) {
    return normal[1].trim().replace(/^"(.*)"$/, "$1");
  }

  return null;
}

export async function fetchVehicleFileForPreview(fileUrl) {
  if (!fileUrl) return null;

  const res = await api.get("/api/vehicles/files/view", {
    params: { fileUrl },
    responseType: "blob",
    timeout: 120000,
  });

  const blob = res.data;
  const contentType = res.headers?.["content-type"] || null;
  const filename =
    parseFilenameFromContentDisposition(
      res.headers?.["content-disposition"]
    ) || "file";

  return { blob, contentType, filename };
}

export async function downloadVehicleFile(fileUrl, preferredName) {
  if (!fileUrl) return;

  const res = await api.get("/api/vehicles/files/download", {
    params: { fileUrl },
    responseType: "blob",
    timeout: 120000,
  });

  const blob = res.data;
  const contentType = res.headers?.["content-type"] || null;
  const filename =
    preferredName ||
    parseFilenameFromContentDisposition(
      res.headers?.["content-disposition"]
    ) ||
    "file";

  const objectUrl = URL.createObjectURL(
    contentType ? new Blob([blob], { type: contentType }) : blob
  );

  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
  }
}

export async function getPresignedViewUrl(fileUrl) {
  if (!fileUrl) return null;
  const { data } = await api.get("/api/vehicles/files/presign", {
    params: { fileUrl },
    timeout: 30000,
  });
  return data?.url || null;
}
